import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useBusinessPlan } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { 
  Building2, 
  Users, 
  Shield, 
  BarChart3,
  Plus, 
  Edit, 
  Trash2,
  Search,
  Filter,
  Archive,
  RotateCcw,
  MapPin,
  FileText,
  UserPlus,
  Mail,
  Activity,
  List,
  LayoutGrid
} from "lucide-react";
import ManageTeamDialog from "./ManageTeamDialog";
import AddTeamMemberDialog from "./AddTeamMemberDialog";
import InviteTeamMemberDialog from "./InviteTeamMemberDialog";
import PendingInvitationsCard from "./PendingInvitationsCard";
import RolesManagementTab from "./RolesManagementTab";
import BulkActionsBar from "./BulkActionsBar";
import SubsidiaryCreationWizard from "./SubsidiaryCreationWizard";
import SubsidiaryHierarchyBreadcrumb from "./SubsidiaryHierarchyBreadcrumb";
import EnhancedOverviewDashboard from "./EnhancedOverviewDashboard";
import AuditLogPanel from "./AuditLogPanel";
import DetailedSubsidiaryCard from "./DetailedSubsidiaryCard";
import EnhancedTeamMemberRow from "./EnhancedTeamMemberRow";
import UserProfileModal from "./UserProfileModal";
import SubsidiaryPlansModal from "./SubsidiaryPlansModal";
import SubsidiaryPerformanceModal from "./SubsidiaryPerformanceModal";
import CompanyAnalytics from "./CompanyAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const CompanySettings = () => {
  const { user } = useAuth();
  const { currentPlan, setCurrentPlan } = useBusinessPlan();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  // Overview Tab State
  const [companyInfo, setCompanyInfo] = useState(null);
  const [overviewStats, setOverviewStats] = useState(null);

  // Subsidiaries Tab State
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [subsidiaryStats, setSubsidiaryStats] = useState(null);
  const [subsidiarySearchTerm, setSubsidiarySearchTerm] = useState("");
  const [subsidiaryFilter, setSubsidiaryFilter] = useState("active"); // active, archived, all
  const [subsidiaryViewMode, setSubsidiaryViewMode] = useState("list"); // list, cards
  const [addSubsidiaryOpen, setAddSubsidiaryOpen] = useState(false);
  const [subsidiaryWizardOpen, setSubsidiaryWizardOpen] = useState(false);
  const [editSubsidiaryOpen, setEditSubsidiaryOpen] = useState(false);
  const [editingSubsidiary, setEditingSubsidiary] = useState(null);
  const [subsidiaryForm, setSubsidiaryForm] = useState({
    name: "",
    code: "",
    location: "",
    description: "",
    parent_id: null
  });

  // Team Members Tab State
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [teamViewMode, setTeamViewMode] = useState("enhanced"); // table, enhanced

  // Roles Tab State
  const [roles, setRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  // Phase 1: New Dialog States
  const [manageTeamDialogOpen, setManageTeamDialogOpen] = useState(false);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState(null);
  const [addTeamMemberDialogOpen, setAddTeamMemberDialogOpen] = useState(false);
  const [inviteTeamMemberDialogOpen, setInviteTeamMemberDialogOpen] = useState(false);
  
  // Edit Team Member Dialog State
  const [editTeamMemberDialogOpen, setEditTeamMemberDialogOpen] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [teamMemberForm, setTeamMemberForm] = useState({
    full_name: "",
    email: "",
    department: ""
  });
  
  // Manage Access Dialog State
  const [manageAccessDialogOpen, setManageAccessDialogOpen] = useState(false);
  const [managingAccessMember, setManagingAccessMember] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedSubsidiariesForMember, setSelectedSubsidiariesForMember] = useState([]);
  
  // User Profile Modal State
  const [userProfileModalOpen, setUserProfileModalOpen] = useState(false);
  const [viewingProfileMember, setViewingProfileMember] = useState(null);
  
  // Subsidiary Modals State
  const [subsidiaryPlansModalOpen, setSubsidiaryPlansModalOpen] = useState(false);
  const [subsidiaryPerformanceModalOpen, setSubsidiaryPerformanceModalOpen] = useState(false);
  const [selectedSubsidiaryForModal, setSelectedSubsidiaryForModal] = useState(null);

  // Bulk Operations State
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [selectedSubsidiaries, setSelectedSubsidiaries] = useState([]);

  useEffect(() => {
    if (user) {
      fetchInitialData();
    }
  }, [user]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchCompanyInfo(),
        fetchOverviewStats(),
        fetchSubsidiaries(),
        fetchSubsidiaryStats(),
        fetchTeamMembers(),
        fetchRoles()
      ]);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load company settings");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // OVERVIEW TAB - DATA FETCHING
  // ============================================================================

  const fetchCompanyInfo = async () => {
    try {
      // Token is already set globally in axios defaults by AuthContext
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/${user.id}`);
      setCompanyInfo(response.data);
    } catch (error) {
      console.error("Error fetching company info:", error);
    }
  };

  const fetchOverviewStats = async () => {
    try {
      const response = await axios.get(`${API}/statistics/overview`);
      setOverviewStats(response.data);
    } catch (error) {
      console.error("Error fetching overview stats:", error);
    }
  };

  // ============================================================================
  // TEAM MEMBERS TAB - DATA FETCHING
  // ============================================================================

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/company-users`);
      // company-users returns {users: [], total: 0} format
      setTeamMembers(response.data?.users || []);
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  // ============================================================================
  // ROLES TAB - DATA FETCHING
  // ============================================================================

  const fetchRoles = async () => {
    try {
      const [rolesRes, userRolesRes] = await Promise.all([
        axios.get(`${API}/roles?exclude_platform=true`),
        axios.get(`${API}/user-roles`)
      ]);
      setRoles(rolesRes.data || []);
      setUserRoles(userRolesRes.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchUserRoles = async () => {
    try {
      const response = await axios.get(`${API}/user-roles`);
      setUserRoles(response.data || []);
    } catch (error) {
      console.error("Error fetching user roles:", error);
    }
  };

  // ============================================================================
  // OVERVIEW TAB - RENDER
  // ============================================================================

  const renderOverviewTab = () => {
    return (
      <EnhancedOverviewDashboard
        companyInfo={user}
        subsidiaries={subsidiaries}
        teamMembers={teamMembers}
        roles={roles}
        userRoles={userRoles}
        onRefresh={fetchCompanyInfo}
        onCreateSubsidiary={() => setSubsidiaryWizardOpen(true)}
        onAddTeamMember={() => setAddTeamMemberDialogOpen(true)}
        onCreateRole={() => {
          setActiveTab("roles");
          // Trigger create role dialog after switching tabs
          setTimeout(() => {
            const createButton = document.querySelector('[data-create-role-button]');
            if (createButton) createButton.click();
          }, 100);
        }}
        onCreateBusinessPlan={() => {
          // Navigate to business plans
          window.location.href = "/dashboard";
        }}
        onViewAllSubsidiaries={() => setActiveTab("subsidiaries")}
        onViewAllTeamMembers={() => setActiveTab("team")}
      />
    );
  };

  // ============================================================================
  // BULK OPERATIONS - HELPER FUNCTIONS
  // ============================================================================

  // Team Members Selection
  const handleSelectTeamMember = (memberId) => {
    setSelectedTeamMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId) 
        : [...prev, memberId]
    );
  };

  const handleSelectAllTeamMembers = (members) => {
    if (selectedTeamMembers.length === members.length) {
      setSelectedTeamMembers([]);
    } else {
      setSelectedTeamMembers(members.map(m => m.id));
    }
  };

  const clearTeamMemberSelection = () => {
    setSelectedTeamMembers([]);
  };

  // Subsidiaries Selection
  const handleSelectSubsidiary = (subsidiaryId) => {
    setSelectedSubsidiaries(prev => 
      prev.includes(subsidiaryId) 
        ? prev.filter(id => id !== subsidiaryId) 
        : [...prev, subsidiaryId]
    );
  };

  const handleSelectAllSubsidiaries = (subs) => {
    if (selectedSubsidiaries.length === subs.length) {
      setSelectedSubsidiaries([]);
    } else {
      setSelectedSubsidiaries(subs.map(s => s.id));
    }
  };

  const clearSubsidiariesSelection = () => {
    setSelectedSubsidiaries([]);
  };

  // Handle bulk operation completion
  const handleBulkOperationComplete = () => {
    fetchTeamMembers();
    fetchSubsidiaries();
    fetchSubsidiaryStats();
  };

  // ============================================================================
  // TEAM MEMBERS TAB - ACTION HANDLERS
  // ============================================================================

  const handleEditTeamMember = (member) => {
    setEditingTeamMember(member);
    setTeamMemberForm({
      full_name: member.full_name || "",
      email: member.email || "",
      department: member.department || ""
    });
    setEditTeamMemberDialogOpen(true);
  };

  const handleSaveTeamMemberEdit = async () => {
    try {
      if (!teamMemberForm.full_name || !teamMemberForm.email) {
        toast.error("Name and email are required");
        return;
      }

      await axios.put(`${API}/company-users/${editingTeamMember.id}`, teamMemberForm);
      
      toast.success("Team member updated successfully");
      setEditTeamMemberDialogOpen(false);
      setEditingTeamMember(null);
      setTeamMemberForm({ full_name: "", email: "", department: "" });
      await fetchTeamMembers();
    } catch (error) {
      console.error("Error updating team member:", error);
      toast.error(error.response?.data?.detail || "Failed to update team member");
    }
  };

  const handleViewTeamMemberProfile = (member) => {
    setViewingProfileMember(member);
    setUserProfileModalOpen(true);
  };

  const handleManageTeamMemberAccess = async (member) => {
    // Get current roles and subsidiaries for this member
    // Use member.roles from backend if available, otherwise filter from userRoles
    const memberRoles = member.roles || userRoles.filter(ur => ur.user_id === member.id);
    const roleIds = memberRoles.map(r => r.role_id);
    
    // Get subsidiary IDs - from member.subsidiaries if available
    const subsidiaryIds = member.subsidiaries 
      ? member.subsidiaries.map(s => s.subsidiary_id)
      : memberRoles.map(ur => ur.subsidiary_id).filter(Boolean);
    
    setManagingAccessMember(member);
    setSelectedRoles(roleIds);
    setSelectedSubsidiariesForMember(subsidiaryIds);
    setManageAccessDialogOpen(true);
  };

  const handleSaveAccessChanges = async () => {
    try {
      if (selectedRoles.length === 0) {
        toast.error("Please select at least one role");
        return;
      }

      // Remove existing role assignments for this user
      const existingAssignments = userRoles.filter(ur => ur.user_id === managingAccessMember.id);
      for (const assignment of existingAssignments) {
        await axios.delete(`${API}/user-roles/${assignment.id}`);
      }

      // Add new role assignments
      for (const roleId of selectedRoles) {
        for (const subsidiaryId of selectedSubsidiariesForMember) {
          await axios.post(`${API}/user-roles`, {
            user_id: managingAccessMember.id,
            role_id: roleId,
            subsidiary_id: subsidiaryId
          });
        }
        
        // If no subsidiaries selected, assign role without subsidiary
        if (selectedSubsidiariesForMember.length === 0) {
          await axios.post(`${API}/user-roles`, {
            user_id: managingAccessMember.id,
            role_id: roleId
          });
        }
      }

      toast.success("Access updated successfully");
      setManageAccessDialogOpen(false);
      setManagingAccessMember(null);
      setSelectedRoles([]);
      setSelectedSubsidiariesForMember([]);
      await fetchUserRoles();
      await fetchTeamMembers(); // Refresh team list to show updated data
    } catch (error) {
      console.error("Error updating access:", error);
      toast.error(error.response?.data?.detail || "Failed to update access");
    }
  };

  const handleDeactivateTeamMember = async (member) => {
    if (!window.confirm(`Are you sure you want to deactivate ${member.full_name || member.email}? They will lose access to the system.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/company-users/${member.id}`);
      toast.success("Team member deactivated successfully");
      await fetchTeamMembers();
    } catch (error) {
      console.error("Error deactivating team member:", error);
      toast.error(error.response?.data?.detail || "Failed to deactivate team member");
    }
  };

  const handleActivateTeamMember = async (member) => {
    try {
      await axios.put(`${API}/company-users/${member.id}`, {
        is_active: true
      });
      toast.success("Team member activated successfully");
      await fetchTeamMembers();
    } catch (error) {
      console.error("Error activating team member:", error);
      toast.error(error.response?.data?.detail || "Failed to activate team member");
    }
  };

  // ============================================================================
  // TEAM MEMBERS TAB - RENDER
  // ============================================================================

  const renderTeamMembersTab = () => {
    const filteredMembers = teamMembers.filter(member => {
      const searchLower = teamSearchTerm.toLowerCase();
      return !teamSearchTerm || 
        member.full_name?.toLowerCase().includes(searchLower) ||
        member.email?.toLowerCase().includes(searchLower);
    });

    return (
      <div className="space-y-6">
        {/* Pending Invitations */}
        <PendingInvitationsCard onUpdate={() => fetchTeamMembers()} />
        
        {/* Header with Search */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Team Members ({teamMembers.length})</CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search members..."
                    value={teamSearchTerm}
                    onChange={(e) => setTeamSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {/* View Toggle */}
                <div className="flex border rounded-md">
                  <Button
                    variant={teamViewMode === "enhanced" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setTeamViewMode("enhanced")}
                    className="rounded-r-none"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={teamViewMode === "table" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setTeamViewMode("table")}
                    className="rounded-l-none"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setInviteTeamMemberDialogOpen(true)}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Invite Member
                </Button>
                <Button onClick={() => setAddTeamMemberDialogOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Team Member
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredMembers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No team members found</p>
              </div>
            ) : teamViewMode === "enhanced" ? (
              <div className="space-y-4">
                {filteredMembers.map((member) => (
                  <EnhancedTeamMemberRow
                    key={member.id}
                    member={member}
                    userRoles={userRoles}
                    roles={roles}
                    subsidiaries={subsidiaries}
                    isSelected={selectedTeamMembers.includes(member.id)}
                    onSelect={() => handleSelectTeamMember(member.id)}
                    onViewProfile={handleViewTeamMemberProfile}
                    onEdit={handleEditTeamMember}
                    onManageAccess={handleManageTeamMemberAccess}
                    onDeactivate={handleDeactivateTeamMember}
                  />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedTeamMembers.length === filteredMembers.length && filteredMembers.length > 0}
                          onCheckedChange={() => handleSelectAllTeamMembers(filteredMembers)}
                        />
                        <span className="text-sm font-medium">Select All</span>
                      </div>
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map((member) => {
                    // Use member.roles from backend if available, otherwise filter from userRoles
                    const memberRoles = member.roles || userRoles.filter(ur => ur.user_id === member.id);
                    return (
                      <TableRow key={member.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedTeamMembers.includes(member.id)}
                            onCheckedChange={() => handleSelectTeamMember(member.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{member.full_name || "N/A"}</TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <Badge variant={member.is_active ? "default" : "secondary"} className={member.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                            {member.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {memberRoles.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {memberRoles.slice(0, 2).map((role, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {role.role_name || roles.find(r => r.id === role.role_id)?.name || "Unknown"}
                                </Badge>
                              ))}
                              {memberRoles.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{memberRoles.length - 2}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No roles</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(member.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {member.is_active ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditTeamMember(member)}
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleManageTeamMemberAccess(member)}
                                  title="Manage Access"
                                >
                                  <Shield className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeactivateTeamMember(member)}
                                  title="Deactivate"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleActivateTeamMember(member)}
                                title="Activate"
                                className="text-green-600 hover:text-green-700"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Bulk Actions Bar for Team Members */}
        <BulkActionsBar
          selectedItems={selectedTeamMembers}
          onClearSelection={clearTeamMemberSelection}
          onSuccess={handleBulkOperationComplete}
          type="users"
          roles={roles}
          subsidiaries={subsidiaries.filter(s => s.is_active)}
        />
      </div>
    );
  };

  // ============================================================================
  // ROLES & PERMISSIONS TAB - RENDER (Using RolesManagementTab component)
  // ============================================================================

  const renderRolesTab = () => {
    return <RolesManagementTab />;
  };

  const renderRolesTab_OLD = () => {
    return (
      <div className="space-y-6">
        {/* Roles Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Available Roles</CardTitle>
          </CardHeader>
          <CardContent>
            {roles.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No roles available</p>
              </div>
            ) : (
              <div className="space-y-4">
                {roles.map((role) => {
                  const assignedCount = userRoles.filter(ur => ur.role_id === role.id).length;
                  return (
                    <Card key={role.id} className="border">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold">{role.name}</h3>
                              <Badge variant="outline" className="text-xs">
                                Level {role.level}
                              </Badge>
                              <Badge variant="outline" className="text-xs capitalize">
                                {role.role_scope}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{role.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Users className="h-4 w-4" />
                                {assignedCount} users
                              </span>
                              <span className="flex items-center gap-1">
                                <Shield className="h-4 w-4" />
                                {role.permissions?.length || 0} permissions
                              </span>
                            </div>
                            {role.permissions && role.permissions.length > 0 && (
                              <details className="mt-3">
                                <summary className="text-sm text-blue-600 cursor-pointer hover:text-blue-700">
                                  View all permissions
                                </summary>
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {role.permissions.map((perm, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {perm.replace(/_/g, ' ')}
                                    </Badge>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Role Assignments Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Role Assignments Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm font-medium">Total Roles Assigned</span>
                <Badge variant="outline">{userRoles.length}</Badge>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm font-medium">Unique Users with Roles</span>
                <Badge variant="outline">{new Set(userRoles.map(ur => ur.user_id)).size}</Badge>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-sm font-medium">Available Roles</span>
                <Badge variant="outline">{roles.length}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ============================================================================
  // SUBSIDIARIES TAB - DATA FETCHING
  // ============================================================================

  const fetchSubsidiaries = async () => {
    try {
      // Use company_only=true to get only this user's company subsidiaries (optimized)
      const response = await axios.get(`${API}/subsidiaries?company_only=true`);
      setSubsidiaries(response.data || []);
    } catch (error) {
      console.error("Error fetching subsidiaries:", error);
      toast.error("Failed to load subsidiaries");
    }
  };

  const fetchSubsidiaryStats = async () => {
    try {
      const response = await axios.get(`${API}/statistics/subsidiaries`);
      setSubsidiaryStats(response.data);
    } catch (error) {
      console.error("Error fetching subsidiary stats:", error);
    }
  };

  // ============================================================================
  // SUBSIDIARIES TAB - CRUD OPERATIONS
  // ============================================================================

  const handleAddSubsidiary = async () => {
    try {
      if (!subsidiaryForm.name) {
        toast.error("Subsidiary name is required");
        return;
      }

      await axios.post(`${API}/subsidiaries`, subsidiaryForm);

      toast.success("Subsidiary created successfully");
      setAddSubsidiaryOpen(false);
      setSubsidiaryForm({ name: "", code: "", location: "", description: "", parent_id: null });
      await fetchSubsidiaries();
      await fetchSubsidiaryStats();
    } catch (error) {
      console.error("Error creating subsidiary:", error);
      // Handle error properly - extract message from detail array if present
      let errorMsg = "Failed to create subsidiary";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg || e).join(", ");
        } else if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        }
      }
      toast.error(errorMsg);
    }
  };

  const handleEditSubsidiary = async () => {
    try {
      if (!subsidiaryForm.name) {
        toast.error("Subsidiary name is required");
        return;
      }

      await axios.put(`${API}/subsidiaries/${editingSubsidiary.id}`, subsidiaryForm);

      toast.success("Subsidiary updated successfully");
      setEditSubsidiaryOpen(false);
      setEditingSubsidiary(null);
      setSubsidiaryForm({ name: "", code: "", location: "", description: "", parent_id: null });
      await fetchSubsidiaries();
    } catch (error) {
      console.error("Error updating subsidiary:", error);
      // Handle error properly - extract message from detail array if present
      let errorMsg = "Failed to update subsidiary";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg || e).join(", ");
        } else if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        }
      }
      toast.error(errorMsg);
    }
  };

  const handleArchiveSubsidiary = async (subsidiary) => {
    if (!window.confirm(`Are you sure you want to archive "${subsidiary.name}"? This will hide it from active lists but preserve all data.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/subsidiaries/${subsidiary.id}`);

      toast.success("Subsidiary archived successfully");
      await fetchSubsidiaries();
      await fetchSubsidiaryStats();
    } catch (error) {
      console.error("Error archiving subsidiary:", error);
      toast.error(error.response?.data?.detail || "Failed to archive subsidiary");
    }
  };

  const handleRestoreSubsidiary = async (subsidiary) => {
    try {
      await axios.post(`${API}/subsidiaries/${subsidiary.id}/restore`, {});

      toast.success("Subsidiary restored successfully");
      await fetchSubsidiaries();
      await fetchSubsidiaryStats();
    } catch (error) {
      console.error("Error restoring subsidiary:", error);
      toast.error(error.response?.data?.detail || "Failed to restore subsidiary");
    }
  };

  const openEditDialog = (subsidiary) => {
    setEditingSubsidiary(subsidiary);
    setSubsidiaryForm({
      name: subsidiary.name,
      code: subsidiary.code || "",
      location: subsidiary.location || "",
      description: subsidiary.description || "",
      parent_id: subsidiary.parent_id || null
    });
    setEditSubsidiaryOpen(true);
  };

  // Phase 1: Open Manage Team Dialog
  const openManageTeamDialog = (subsidiary) => {
    setSelectedSubsidiary(subsidiary);
    setManageTeamDialogOpen(true);
  };

  // ============================================================================
  // SUBSIDIARIES TAB - FILTERING
  // ============================================================================

  const filteredSubsidiaries = subsidiaries.filter(sub => {
    // Filter by status
    if (subsidiaryFilter === "active" && sub.is_active === false) return false;
    if (subsidiaryFilter === "archived" && sub.is_active !== false) return false;

    // Filter by search term
    if (subsidiarySearchTerm) {
      const searchLower = subsidiarySearchTerm.toLowerCase();
      return (
        sub.name?.toLowerCase().includes(searchLower) ||
        sub.location?.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  // ============================================================================
  // SUBSIDIARIES TAB - ACTION HANDLERS
  // ============================================================================

  const handleViewSubsidiaryPlans = (subsidiary) => {
    setSelectedSubsidiaryForModal(subsidiary);
    setSubsidiaryPlansModalOpen(true);
  };

  const handleViewSubsidiaryPerformance = (subsidiary) => {
    setSelectedSubsidiaryForModal(subsidiary);
    setSubsidiaryPerformanceModalOpen(true);
  };

  // ============================================================================
  // RENDER - SUBSIDIARIES TAB
  // ============================================================================

  const renderSubsidiariesTab = () => {
    return (
      <div className="space-y-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Subsidiaries</p>
                  <p className="text-2xl font-bold">{subsidiaryStats?.total_subsidiaries || 0}</p>
                </div>
                <Building2 className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active</p>
                  <p className="text-2xl font-bold text-green-600">{subsidiaryStats?.active_subsidiaries || 0}</p>
                </div>
                <Building2 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Archived</p>
                  <p className="text-2xl font-bold text-gray-600">{subsidiaryStats?.archived_subsidiaries || 0}</p>
                </div>
                <Archive className="h-8 w-8 text-gray-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Team Members</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {subsidiaryStats?.subsidiaries?.reduce((sum, s) => sum + (s.team_count || 0), 0) || 0}
                  </p>
                </div>
                <Users className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex gap-2 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search subsidiaries..."
                    value={subsidiarySearchTerm}
                    onChange={(e) => setSubsidiarySearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <select
                  value={subsidiaryFilter}
                  onChange={(e) => setSubsidiaryFilter(e.target.value)}
                  className="px-4 py-2 border rounded-md"
                >
                  <option value="active">Active Only</option>
                  <option value="archived">Archived Only</option>
                  <option value="all">All Subsidiaries</option>
                </select>
              </div>

              <div className="flex gap-2">
                {/* View Toggle */}
                <div className="flex border rounded-md">
                  <Button 
                    variant={subsidiaryViewMode === "list" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSubsidiaryViewMode("list")}
                    className="rounded-r-none"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant={subsidiaryViewMode === "cards" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSubsidiaryViewMode("cards")}
                    className="rounded-l-none"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </div>

                <Button variant="outline" onClick={() => setAddSubsidiaryOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Quick Add
                </Button>
                <Button onClick={() => setSubsidiaryWizardOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create with Wizard
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subsidiaries Display */}
        {filteredSubsidiaries.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No subsidiaries found</h3>
                <p className="text-gray-500 mb-4">
                  {subsidiarySearchTerm ? "Try adjusting your search" : "Get started by creating your first subsidiary"}
                </p>
                {!subsidiarySearchTerm && (
                  <Button onClick={() => setAddSubsidiaryOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Subsidiary
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : subsidiaryViewMode === "cards" ? (
          <div className="grid grid-cols-1 gap-6">
            {filteredSubsidiaries.map((subsidiary) => (
              <DetailedSubsidiaryCard
                key={subsidiary.id}
                subsidiary={subsidiary}
                subsidiaries={subsidiaries}
                onManageTeam={openManageTeamDialog}
                onEdit={openEditDialog}
                onArchive={handleArchiveSubsidiary}
                onViewPlans={handleViewSubsidiaryPlans}
                onViewPerformance={handleViewSubsidiaryPerformance}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Subsidiaries</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedSubsidiaries.length === filteredSubsidiaries.length && filteredSubsidiaries.length > 0}
                          onCheckedChange={() => handleSelectAllSubsidiaries(filteredSubsidiaries)}
                        />
                        <span className="text-sm font-medium">Select All</span>
                      </div>
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Team Members</TableHead>
                    <TableHead>Business Plans</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubsidiaries.map((subsidiary) => {
                    const stats = subsidiaryStats?.subsidiaries?.find(s => s.id === subsidiary.id);
                    return (
                      <TableRow key={subsidiary.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedSubsidiaries.includes(subsidiary.id)}
                            onCheckedChange={() => handleSelectSubsidiary(subsidiary.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <SubsidiaryHierarchyBreadcrumb 
                              subsidiary={subsidiary} 
                              subsidiaries={subsidiaries}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          {subsidiary.location ? (
                            <div className="flex items-center gap-1 text-gray-600">
                              <MapPin className="h-3 w-3" />
                              {subsidiary.location}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="flex items-center gap-1 w-fit">
                            <Users className="h-3 w-3" />
                            {subsidiary.team_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{stats?.plan_count || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          {subsidiary.is_active === false ? (
                            <Badge variant="secondary">Archived</Badge>
                          ) : (
                            <Badge variant="success" className="bg-green-100 text-green-800">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {subsidiary.is_active !== false ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openManageTeamDialog(subsidiary)}
                                  title="Manage Team"
                                >
                                  <Users className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditDialog(subsidiary)}
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleArchiveSubsidiary(subsidiary)}
                                  title="Archive"
                                >
                                  <Archive className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRestoreSubsidiary(subsidiary)}
                                title="Restore"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Add Subsidiary Dialog */}
        <Dialog open={addSubsidiaryOpen} onOpenChange={setAddSubsidiaryOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Subsidiary</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Subsidiary Name *</Label>
                <Input
                  id="name"
                  value={subsidiaryForm.name}
                  onChange={(e) => setSubsidiaryForm({...subsidiaryForm, name: e.target.value})}
                  placeholder="e.g., North Region"
                />
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={subsidiaryForm.location}
                  onChange={(e) => setSubsidiaryForm({...subsidiaryForm, location: e.target.value})}
                  placeholder="e.g., New York, USA"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={subsidiaryForm.description}
                  onChange={(e) => setSubsidiaryForm({...subsidiaryForm, description: e.target.value})}
                  placeholder="Brief description of this subsidiary..."
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => {
                setAddSubsidiaryOpen(false);
                setSubsidiaryForm({ name: "", code: "", location: "", description: "", parent_id: null });
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddSubsidiary}>
                Create Subsidiary
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Subsidiary Dialog */}
        <Dialog open={editSubsidiaryOpen} onOpenChange={setEditSubsidiaryOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Subsidiary</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-name">Subsidiary Name *</Label>
                <Input
                  id="edit-name"
                  value={subsidiaryForm.name}
                  onChange={(e) => setSubsidiaryForm({...subsidiaryForm, name: e.target.value})}
                  placeholder="e.g., North Region"
                />
              </div>
              <div>
                <Label htmlFor="edit-code">Subsidiary Code *</Label>
                <Input
                  id="edit-code"
                  value={subsidiaryForm.code}
                  onChange={(e) => setSubsidiaryForm({...subsidiaryForm, code: e.target.value})}
                  placeholder="e.g., NORTH-001"
                />
              </div>
              <div>
                <Label htmlFor="edit-location">Location</Label>
                <Input
                  id="edit-location"
                  value={subsidiaryForm.location}
                  onChange={(e) => setSubsidiaryForm({...subsidiaryForm, location: e.target.value})}
                  placeholder="e.g., New York, USA"
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={subsidiaryForm.description}
                  onChange={(e) => setSubsidiaryForm({...subsidiaryForm, description: e.target.value})}
                  placeholder="Brief description of this subsidiary..."
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => {
                setEditSubsidiaryOpen(false);
                setEditingSubsidiary(null);
                setSubsidiaryForm({ name: "", code: "", location: "", description: "", parent_id: null });
              }}>
                Cancel
              </Button>
              <Button onClick={handleEditSubsidiary}>
                Update Subsidiary
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Actions Bar for Subsidiaries */}
        <BulkActionsBar
          selectedItems={selectedSubsidiaries}
          onClearSelection={clearSubsidiariesSelection}
          onSuccess={handleBulkOperationComplete}
          type="subsidiaries"
          roles={roles}
          subsidiaries={subsidiaries.filter(s => s.is_active)}
        />
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading company settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Company Settings</h1>
        <p className="text-gray-600 mt-2">Manage your company structure, team members, and permissions</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="subsidiaries">
            <Building2 className="h-4 w-4 mr-2" />
            Subsidiaries
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="h-4 w-4 mr-2" />
            Team Members
          </TabsTrigger>
          <TabsTrigger value="roles">
            <Shield className="h-4 w-4 mr-2" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="audit">
            <Activity className="h-4 w-4 mr-2" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {renderOverviewTab()}
        </TabsContent>

        <TabsContent value="subsidiaries">
          {renderSubsidiariesTab()}
        </TabsContent>

        <TabsContent value="team">
          {renderTeamMembersTab()}
        </TabsContent>

        <TabsContent value="roles">
          {renderRolesTab()}
        </TabsContent>

        <TabsContent value="analytics">
          <CompanyAnalytics subsidiaries={subsidiaries} />
        </TabsContent>

        <TabsContent value="audit">
          <AuditLogPanel />
        </TabsContent>
      </Tabs>

      {/* Phase 1: New Dialogs */}
      <ManageTeamDialog
        subsidiary={selectedSubsidiary}
        open={manageTeamDialogOpen}
        onClose={() => {
          setManageTeamDialogOpen(false);
          setSelectedSubsidiary(null);
        }}
        onUpdate={() => {
          fetchSubsidiaries(); // Refresh to update team counts
          fetchTeamMembers(); // Refresh team list
        }}
      />

      <AddTeamMemberDialog
        open={addTeamMemberDialogOpen}
        onClose={() => setAddTeamMemberDialogOpen(false)}
        onSuccess={() => {
          fetchTeamMembers(); // Refresh team list
          fetchUserRoles(); // Refresh user roles to show new assignments
          fetchSubsidiaries(); // Refresh subsidiary counts
          setAddTeamMemberDialogOpen(false);
        }}
      />

      <InviteTeamMemberDialog
        open={inviteTeamMemberDialogOpen}
        onClose={() => setInviteTeamMemberDialogOpen(false)}
        onSuccess={() => {
          setInviteTeamMemberDialogOpen(false);
          // Invitations will appear in PendingInvitationsCard
        }}
      />

      <SubsidiaryCreationWizard
        open={subsidiaryWizardOpen}
        onClose={() => setSubsidiaryWizardOpen(false)}
        onSuccess={() => {
          fetchSubsidiaries();
          fetchSubsidiaryStats();
          setSubsidiaryWizardOpen(false);
        }}
        subsidiaries={subsidiaries}
      />

      {/* Edit Team Member Dialog */}
      <Dialog open={editTeamMemberDialogOpen} onOpenChange={setEditTeamMemberDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-full-name">Full Name *</Label>
              <Input
                id="edit-full-name"
                value={teamMemberForm.full_name}
                onChange={(e) => setTeamMemberForm({...teamMemberForm, full_name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={teamMemberForm.email}
                onChange={(e) => setTeamMemberForm({...teamMemberForm, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label htmlFor="edit-department">Department</Label>
              <Input
                id="edit-department"
                value={teamMemberForm.department}
                onChange={(e) => setTeamMemberForm({...teamMemberForm, department: e.target.value})}
                placeholder="e.g., Sales, Engineering"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => {
              setEditTeamMemberDialogOpen(false);
              setEditingTeamMember(null);
              setTeamMemberForm({ full_name: "", email: "", department: "" });
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveTeamMemberEdit}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Access Dialog */}
      <Dialog open={manageAccessDialogOpen} onOpenChange={setManageAccessDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Access - {managingAccessMember?.full_name || managingAccessMember?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Roles Selection */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Roles</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                {roles.filter(r => r.name !== "Super Admin").map((role) => (
                  <div key={role.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`role-${role.id}`}
                      checked={selectedRoles.includes(role.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedRoles([...selectedRoles, role.id]);
                        } else {
                          setSelectedRoles(selectedRoles.filter(id => id !== role.id));
                        }
                      }}
                    />
                    <Label htmlFor={`role-${role.id}`} className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{role.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {role.role_scope}
                        </Badge>
                      </div>
                      {role.description && (
                        <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                      )}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Subsidiaries Selection */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Subsidiaries (Optional)</Label>
              <p className="text-sm text-gray-500 mb-3">Select subsidiaries this user can access. Leave empty for company-wide access.</p>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                {subsidiaries.filter(s => s.is_active !== false).map((subsidiary) => (
                  <div key={subsidiary.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`sub-${subsidiary.id}`}
                      checked={selectedSubsidiariesForMember.includes(subsidiary.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedSubsidiariesForMember([...selectedSubsidiariesForMember, subsidiary.id]);
                        } else {
                          setSelectedSubsidiariesForMember(selectedSubsidiariesForMember.filter(id => id !== subsidiary.id));
                        }
                      }}
                    />
                    <Label htmlFor={`sub-${subsidiary.id}`} className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span>{subsidiary.name}</span>
                        {subsidiary.location && (
                          <span className="text-xs text-gray-400">({subsidiary.location})</span>
                        )}
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                <strong>Selected:</strong> {selectedRoles.length} role(s), {selectedSubsidiariesForMember.length} subsidiaries
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => {
              setManageAccessDialogOpen(false);
              setManagingAccessMember(null);
              setSelectedRoles([]);
              setSelectedSubsidiariesForMember([]);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveAccessChanges}>
              Save Access Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Profile Modal */}
      <UserProfileModal
        open={userProfileModalOpen}
        onClose={() => {
          setUserProfileModalOpen(false);
          setViewingProfileMember(null);
        }}
        member={viewingProfileMember}
        userRoles={userRoles}
        roles={roles}
        subsidiaries={subsidiaries}
        onEdit={handleEditTeamMember}
        onManageAccess={handleManageTeamMemberAccess}
      />

      {/* Subsidiary Plans Modal */}
      <SubsidiaryPlansModal
        open={subsidiaryPlansModalOpen}
        onClose={() => {
          setSubsidiaryPlansModalOpen(false);
          setSelectedSubsidiaryForModal(null);
        }}
        subsidiary={selectedSubsidiaryForModal}
        currentPlan={currentPlan}
        onSwitchPlan={setCurrentPlan}
      />

      {/* Subsidiary Performance Modal */}
      <SubsidiaryPerformanceModal
        open={subsidiaryPerformanceModalOpen}
        onClose={() => {
          setSubsidiaryPerformanceModalOpen(false);
          setSelectedSubsidiaryForModal(null);
        }}
        subsidiary={selectedSubsidiaryForModal}
        subsidiaries={subsidiaries}
        teamMembers={teamMembers}
        userRoles={userRoles}
      />
    </div>
  );
};

export default CompanySettings;
